import { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { IssueInvoiceDialog } from "@/components/admin/AdminAccounts";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { PLAN_KEYS } from "@/lib/plans";
import { SPECIALTIES } from "@/lib/clinic";
import { BigButton, Chip, Field, inputCls } from "@/components/ui-kit";

export default function ShopPlanDialog({ shop, onClose, onChanged }) {
  const { t } = useApp();
  const [plan, setPlan] = useState("pro");
  const [months, setMonths] = useState(1);
  const [days, setDays] = useState(14);
  const [scope, setScope] = useState("full");
  const [inv, setInv] = useState(false);
  useEffect(() => { if (shop) { setPlan(shop.plan === "free" ? "pro" : shop.plan); setMonths(1); setDays(14); setScope(shop.clinic_scope || "full"); } }, [shop]);
  const call = async (path, body) => {
    try { await api.post(`/admin/shops/${shop.id}/${path}`, body); toast.success(t("plan_updated")); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!shop} onOpenChange={(o) => !o && onClose()}>
      {shop && (
        <DialogContent className="max-w-md" data-testid="admin-plan-dialog">
          <DialogTitle className="font-heading text-2xl">{shop.name}</DialogTitle>
          <Field label={t("plan")}>
            <div className="flex gap-2">{PLAN_KEYS.map((k) => <Chip key={k} active={plan === k} onClick={() => setPlan(k)} data-testid={`admin-plan-${k}`}>{t(`plan_${k}`)}</Chip>)}</div>
          </Field>
          {plan !== "free" && <Field label={t("months")}><input type="number" min={1} max={36} value={months} onChange={(e) => setMonths(e.target.value)} className={inputCls} data-testid="admin-plan-months" /></Field>}
          <BigButton onClick={() => call("plan", { plan, months: Number(months) || 1 })} data-testid="admin-apply-plan">{t("apply_plan")}</BigButton>
          <BigButton variant="soft" onClick={() => setInv(true)} data-testid="admin-issue-invoice"><FileText className="w-5 h-5" />{t("inv_issue")}</BigButton>
          {inv && <IssueInvoiceDialog shop={shop} onClose={() => setInv(false)} onDone={onChanged} />}
          <Field label={t("extend_trial")}>
            <div className="flex gap-2">
              <input type="number" min={1} max={90} value={days} onChange={(e) => setDays(e.target.value)} className={inputCls} data-testid="admin-trial-days" />
              <BigButton variant="soft" onClick={() => call("trial", { days: Number(days) || 1 })} data-testid="admin-extend-trial">{t("extend")}</BigButton>
            </div>
          </Field>
          {shop.type === "clinic" && (
            <Field label={t("clinic_scope")}>
              <div className="flex gap-2 flex-wrap mb-2">
                <Chip active={scope === "full"} onClick={() => setScope("full")} data-testid="admin-scope-full">{t("scope_full")}</Chip>
                <Chip active={scope !== "full"} onClick={() => setScope(scope === "full" ? "gynecology" : scope)} data-testid="admin-scope-specialty">{t("scope_specialty")}</Chip>
              </div>
              <div className="flex gap-2">
                {scope !== "full" && <select value={scope} onChange={(e) => setScope(e.target.value)} className={inputCls} data-testid="admin-scope-select">{SPECIALTIES.map((s) => <option key={s} value={s}>{t(`sp_${s}`)}</option>)}</select>}
                <BigButton variant="soft" onClick={() => call("scope", { clinic_scope: scope })} data-testid="admin-apply-scope">{t("apply")}</BigButton>
              </div>
              <p className="text-xs text-[#555] mt-1">{t("scope_hint")}</p>
            </Field>
          )}
          <label className="flex items-center justify-between rounded-xl bg-destructive/5 p-3 font-bold text-destructive">
            {t("suspend_store")}
            <Switch checked={!!shop.suspended} onCheckedChange={(v) => call("suspend", { suspended: v })} data-testid="admin-suspend-switch" />
          </label>
        </DialogContent>
      )}
    </Dialog>
  );
}
